import { z } from "zod";
import {
    FilesQuery,
    storageResultsPerPageOptions,
    zStorageFilterByType,
    zStorageOrderBy,
    zStorageSortBy,
} from "../types";
import { View } from "../components/grids/types";

const zView: z.ZodType<View> = z.enum(["grid", "list"]);
const zQueryPreferences = z.object({
    resultsPerPage: z
        .number()
        .refine((v) => storageResultsPerPageOptions.includes(v)),
    orderBy: zStorageOrderBy,
    sortBy: zStorageSortBy,
});
const zFoldersViewingOptions = z.object({
    query: zQueryPreferences.partial().optional(),
    view: zView.optional(),
});
const zFilesViewingOptions = z.object({
    query: zQueryPreferences
        .extend({ filterBy: zStorageFilterByType })
        .partial()
        .optional(),
    view: zView.optional(),
});
const zFolderViewingPreferences = z.object({
    folders: zFoldersViewingOptions.optional(),
    files: zFilesViewingOptions.optional(),
});
const zStorageViewingPreferences = z.record(
    z.string(),
    zFolderViewingPreferences
);

export type FilesViewingOptions = z.infer<typeof zFilesViewingOptions>;
export type FolderViewingPreferences = z.infer<typeof zFolderViewingPreferences>;
export type ViewingScope = keyof FolderViewingPreferences;

export const ROOT_FOLDER_KEY = "root";

const STORAGE_KEY = "storage-viewing-preferences-by-folder";
const LEGACY_STORAGE_KEY = "storage-viewing-preferences";

/** The subset of a query change worth remembering (search and page are
 *  transient). Returns null when the change touches none of it. */
export function pickPersistableQuery(
    changes: Partial<FilesQuery>
): FilesViewingOptions["query"] | null {
    const { resultsPerPage, orderBy, sortBy, filterBy } = changes;
    const persistable = Object.fromEntries(
        Object.entries({ resultsPerPage, orderBy, sortBy, filterBy }).filter(
            ([, value]) => value !== undefined
        )
    );
    return Object.keys(persistable).length ? persistable : null;
}

export type ViewingPreferencesStore = {
    load: (folderKey: string) => FolderViewingPreferences;
    save: (
        folderKey: string,
        scope: ViewingScope,
        changes: FilesViewingOptions
    ) => void;
};

const mergeFolderPreferences = (
    current: FolderViewingPreferences,
    scope: ViewingScope,
    changes: FilesViewingOptions
): FolderViewingPreferences => ({
    ...current,
    [scope]: {
        ...current[scope],
        ...changes,
        ...(changes.query && {
            query: { ...current[scope]?.query, ...changes.query },
        }),
    },
});

const readAll = (): z.infer<typeof zStorageViewingPreferences> => {
    try {
        localStorage.removeItem(LEGACY_STORAGE_KEY);
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        const parsed = zStorageViewingPreferences.safeParse(JSON.parse(raw));
        return parsed.success ? parsed.data : {};
    } catch {
        return {};
    }
};

const localStorageStore: ViewingPreferencesStore = {
    load: (folderKey) => readAll()[folderKey] ?? {},
    save: (folderKey, scope, changes) => {
        try {
            const all = readAll();
            all[folderKey] = mergeFolderPreferences(
                all[folderKey] ?? {},
                scope,
                changes
            );
            localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
        } catch {
            // storage may be unavailable (private mode, quota)
        }
    },
};

/** Every folder remembers its own viewing choices. The storage page keeps
 *  them in localStorage; the explorer modal only while it is open. */
export const createViewingPreferencesStore = (
    persist: boolean
): ViewingPreferencesStore => {
    if (persist) return localStorageStore;

    const memory = new Map<string, FolderViewingPreferences>();
    return {
        load: (folderKey) => memory.get(folderKey) ?? {},
        save: (folderKey, scope, changes) => {
            memory.set(
                folderKey,
                mergeFolderPreferences(
                    memory.get(folderKey) ?? {},
                    scope,
                    changes
                )
            );
        },
    };
};
